import {
  documentDetailsService,
  documentFullTextSearchService,
  filterCriteriaService,
  getDocumentsService,
  getDocumentCategoryService,
  getDocumentTypeService,
  getPersonsService,
  getFavoriteFiltersService,
  documentUploadStatus,
  listFolderTree,
  createFolder,
  deleteFolder,
  renameFolder,
  moveDocument,
  listFileFormats,
} from 'components/Dashboard/Documents/services/documentServices';
import {
  getFilterCriteria,
  filteredDocuments,
  filterFailed,
  documentDetailsState,
  getDocumentDetailFailed,
  getSearchResult,
  searchResultState,
  getSearchResultFailed,
  getDocumentList,
  getPersons,
  listDocumentsState,
  getDocumentListFailed,
  onFiltering,
  getDocumentCategory,
  getDocumentTypes,
  getFavoriteFilters,
  getFavoriteFiltersFailed,
  listFavoriteFiltersState,
  taskId,
  uploadStatus,
  minimumLoadTimeReached,
  loadingFolderTree,
  listFoldersTree,
  failedToLoadTree,
  getFileFormats,
} from './documentSlice';

import { setMsgInfo } from 'actions';
import { LIST_ALL_DOCUMENTS } from 'utils/apiEndPoints';

export const filterCriteriaThunk = (filter_param) => async (dispatch) => {
  try {
    const cleanedParams = Object.entries(filter_param).reduce(
      (acc, [key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          acc[key] = value;
        }
        return acc;
      },
      {},
    );

    dispatch(getFilterCriteria());

    const data = await filterCriteriaService(cleanedParams);

    dispatch(filteredDocuments(data));
  } catch (error) {
    dispatch(filterFailed());
  }
};

export const onFilterCriteriaThunk = (isFiltering) => async (dispatch) => {
  try {
    dispatch(onFiltering(isFiltering));
  } catch (error) {
    throw error;
  }
};

export const documentDetailsThunk = (id) => async (dispatch) => {
  try {
    const data = await documentDetailsService(id);

    dispatch(documentDetailsState(data));
  } catch (error) {
    dispatch(getDocumentDetailFailed());
  }
};

export const documentClearDetailsThunk = () => async (dispatch) => {
  try {
    dispatch(documentDetailsState({}));
  } catch (error) {
    dispatch(getDocumentDetailFailed());
  }
};

export const fullTextSearchThunk = (search_query) => async (dispatch) => {
  try {
    dispatch(getSearchResult());

    const data = await documentFullTextSearchService(search_query);

    dispatch(searchResultState(data));
  } catch (error) {
    dispatch(getSearchResultFailed());
  }
};

export const listDocumentsThunk = () => async (dispatch, getState) => {
  const MIN_LOADING_TIME = 10000; // 1 second minimum loading time

  try {
    dispatch(getDocumentList());

    const loadingTimer = setTimeout(() => {
      dispatch(minimumLoadTimeReached());
    }, MIN_LOADING_TIME);

    const state = getState().document;
    const selectedFolder = state.selectedFolder;

    let url = LIST_ALL_DOCUMENTS;

    if (selectedFolder) {
      url += `?folder=${selectedFolder.id}`;
    }

    const data = await getDocumentsService(url);

    clearTimeout(loadingTimer);
    dispatch(listDocumentsState(data));
    dispatch(minimumLoadTimeReached());
  } catch (error) {
    dispatch(getDocumentListFailed(error.message));
    dispatch(minimumLoadTimeReached());
  }
};

export const listDocumentCategoriesThunk = () => async (dispatch) => {
  try {
    const data = await getDocumentCategoryService();

    dispatch(getDocumentCategory(data));
  } catch (error) {
    throw error;
  }
};

export const listDocumentTypesThunk = () => async (dispatch) => {
  try {
    const data = await getDocumentTypeService();

    dispatch(getDocumentTypes(data));
  } catch (error) {
    throw error;
  }
};

export const listPersonsThunk = () => async (dispatch) => {
  try {
    const data = await getPersonsService();

    dispatch(getPersons(data));
  } catch (error) {
    throw error;
  }
};

export const listFavoriteFiltersThunk = () => async (dispatch) => {
  try {
    dispatch(getFavoriteFilters());

    const data = await getFavoriteFiltersService();

    dispatch(listFavoriteFiltersState(data));
  } catch (error) {
    dispatch(getFavoriteFiltersFailed());

    throw error;
  }
};

export const getTaskIdThunk = (task_Id) => (dispatch) => {
  dispatch(taskId(task_Id));
};

export const uploadStatusThunk = (task_id) => async (dispatch) => {
  try {
    // console.log('calling [uploadStatusThunk]');
    const status = await documentUploadStatus(task_id);
    // console.log('calling [uploadStatusThunk]: ', status);
    dispatch(uploadStatus(status));
  } catch (error) {
    dispatch(uploadStatus('failed'));
    console.error('[uploadStatusThunk] error: ', error);
    throw error;
  }
};

export const listFolderTreeThunk = () => async (dispatch) => {
  try {
    dispatch(loadingFolderTree());

    const { data } = await listFolderTree();

    dispatch(listFoldersTree(data));
  } catch (error) {
    dispatch(failedToLoadTree());

    throw error;
  }
};

export const createFolderThunk =
  (name, parentId = null) =>
  async (dispatch) => {
    try {
      await createFolder({ name: name, parent: parentId });
      dispatch(listFolderTreeThunk());
    } catch (error) {
      dispatch(
        setMsgInfo({
          success: false,
          msg: `Create failed: ${error}`,
        }),
      );
      throw error;
    }
  };

export const renameFolderThunk = (folderId, newName) => async (dispatch) => {
  try {
    await renameFolder({ folderId, newName });
    dispatch(listFolderTreeThunk());
  } catch (error) {
    dispatch(
      setMsgInfo({
        success: false,
        msg: `Rename failed: ${error}`,
      }),
    );
    throw error;
  }
};

export const deleteFolderThunk = (folderId) => async (dispatch) => {
  try {
    await deleteFolder({
      folderId: folderId,
    });

    dispatch(listFolderTreeThunk());
    dispatch(listDocumentsThunk());
  } catch (error) {
    dispatch(
      setMsgInfo({
        success: false,
        msg: `Delete failed: ${error}`,
      }),
    );
    throw error;
  }
};

export const moveDocumentThunk = (documentId, newFolderId) => async (dispatch) => {
  try {
    await moveDocument({
      documentId,
      newFolderId,
    });

    dispatch(listDocumentsThunk());
  } catch (error) {
    dispatch(
      setMsgInfo({
        success: false,
        msg: `Failed move document: ${error}`,
      }),
    );
    throw error;
  }
};

export const listFileFormatsThunk = () => async (dispatch) => {
  try {
    const data = await listFileFormats();

    dispatch(getFileFormats(data));
  } catch (error) {
    throw error;
  }
};
